import Button from "components/Button/Button";
import Card from "components/Card/Card";
import { KaboodleToaster } from "components/common";
import React, { useCallback, useEffect } from "react";
import {
  useDisconnectQuickBooksMutation,
  useInitiateOAuthFlowMutation,
} from "services/quickbooks-api";
import { useToken } from "utils/context";

import { getEnvironmentDisplay, isProduction } from "./quickBooksConfig";
import useQuickBooksConnectionStatus from "./useQuickBooksConnectionStatus";
import useQuickBooksReconnect from "./useQuickBooksReconnect";

const QuickBooksOAuth: React.FC = () => {
  const { accessToken } = useToken();
  const [initiateOAuthFlow] = useInitiateOAuthFlowMutation();
  const [disconnectQuickBooks] = useDisconnectQuickBooksMutation();

  const { connectionStatus, isLoading } = useQuickBooksConnectionStatus();

  const { startReconnection } = useQuickBooksReconnect();

  useEffect(() => {
    if (connectionStatus?.error_message) {
      console.error(
        "QuickBooks connection error:",
        connectionStatus.error_message,
      );

      KaboodleToaster.error("QuickBooks connection needs to be refreshed");
    }
  }, [connectionStatus?.error_message]);

  const handleConnect = useCallback(async () => {
    if (!accessToken) return;

    try {
      // send initial POST to initiateOAuthFlow
      const response = await initiateOAuthFlow().unwrap();
      if (response?.url) {
        // redirect to the QuickBooks OAuth URL
        window.location.href = response.url;
      }
    } catch (error) {
      console.error("Error initiating QuickBooks OAuth:", error);
      KaboodleToaster.error("Failed to connect to QuickBooks");
    }
  }, [accessToken, initiateOAuthFlow]);

  const handleDisconnect = useCallback(async () => {
    if (!accessToken) return;

    try {
      await disconnectQuickBooks().unwrap();
      KaboodleToaster.success("Successfully disconnected from QuickBooks");
    } catch (error) {
      console.error("Error disconnecting from QuickBooks:", error);

      // extract specific error message if available
      const errorObj = error as { status?: number; data?: { error?: string } };
      const errorMessage =
        errorObj?.data?.error || "Failed to disconnect from QuickBooks";

      KaboodleToaster.error(errorMessage);
    }
  }, [accessToken, disconnectQuickBooks]);

  const handleReconnect = useCallback(async () => {
    await startReconnection();
  }, [startReconnection]);

  const renderContent = () => {
    if (isLoading) {
      return <p>Checking QuickBooks connection status...</p>;
    }

    const envDisplay = getEnvironmentDisplay();
    const envWarning = !isProduction() && (
      <div className="alert alert-warning mt-2">
        <small>
          This is a non-production environment. QuickBooks integration will use
          sandbox credentials.
        </small>
      </div>
    );

    // if we have an error message, show a reconnect button
    if (connectionStatus?.error_message) {
      return (
        <>
          <p className="text-warning">
            ⚠️ QuickBooks connection requires authorization renewal
          </p>
          <p className="text-muted">
            {connectionStatus.error_message.split("(Ref")[0]}
          </p>
          {envWarning}
          <Button onClick={handleReconnect} className="btn-primary">
            Reconnect QuickBooks
          </Button>
        </>
      );
    }

    if (connectionStatus?.is_connected) {
      return (
        <>
          <p className="text-success">✓ Connected to QuickBooks {envDisplay}</p>
          {envWarning}
          <div
            className="d-flex gap-2"
            style={{ display: "flex", marginTop: "1rem" }}
          >
            <Button onClick={handleReconnect} className="btn-primary">
              Refresh Connection
            </Button>
            <Button onClick={handleDisconnect} className="delete">
              Disconnect QuickBooks
            </Button>
          </div>
        </>
      );
    }

    return (
      <>
        <p>
          Connect your QuickBooks account to enable invoice syncing {envDisplay}
        </p>
        {envWarning}
        <Button onClick={handleConnect}>Connect QuickBooks</Button>
      </>
    );
  };

  return (
    <Card heading="QuickBooks Integration">
      <div className="card__body">{renderContent()}</div>
    </Card>
  );
};

export default QuickBooksOAuth;
